import { Controller } from "@hotwired/stimulus"

// Auto-upgrades all <select> elements within its scope to custom dropdowns.
// Attach to <body> to upgrade every select on the page.
//
// Usage:
//   <body data-controller="custom-select">
//
// Skips: select[multiple], select[size], select[data-no-upgrade]
// Restores native selects before Turbo caches the page (prevents duplicates).

export default class extends Controller {
  connect() {
    this.upgradedSelects = new Set()
    this.handleOutsideClick = this.closeAll.bind(this)
    this.handleEscape = this.onKeydownGlobal.bind(this)
    this.handleTurboCache = this.restoreAll.bind(this)

    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleEscape)
    document.addEventListener('turbo:before-cache', this.handleTurboCache)

    this.upgradeAll()
    this.observer = new MutationObserver(() => this.upgradeAll())
    this.observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleEscape)
    document.removeEventListener('turbo:before-cache', this.handleTurboCache)
    if (this.observer) this.observer.disconnect()
    this.restoreAll()
  }

  // Restore all native selects (for Turbo cache and disconnect)
  restoreAll() {
    this.upgradedSelects.forEach(select => this.downgrade(select))
    this.upgradedSelects.clear()
  }

  upgradeAll() {
    // Prune selects no longer in the DOM
    for (const select of this.upgradedSelects) {
      if (!document.contains(select)) {
        if (select._cs) select._cs.wrapper.remove()
        this.upgradedSelects.delete(select)
      }
    }

    this.element.querySelectorAll('select').forEach(select => {
      if (this.shouldSkip(select)) return
      if (this.upgradedSelects.has(select)) return
      this.upgrade(select)
    })
  }

  shouldSkip(select) {
    if (select.multiple) return true
    if (select.getAttribute('size')) return true
    if (select.dataset.noUpgrade !== undefined) return true
    // Skip selects hidden by their own style or a hidden ancestor
    if (select.offsetParent === null && !select.dataset.upgraded) return true
    return false
  }

  upgrade(select) {
    this.upgradedSelects.add(select)
    select.style.display = 'none'
    select.dataset.upgraded = 'true'

    const wrapper = document.createElement('div')
    wrapper.className = 'cs-wrapper'

    const trigger = document.createElement('button')
    trigger.type = 'button'
    trigger.className = 'cs-trigger'
    this.syncTriggerText(trigger, select)
    this.syncTriggerWidth(trigger, select)

    const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    chevron.setAttribute('viewBox', '0 0 16 16')
    chevron.setAttribute('fill', 'currentColor')
    chevron.setAttribute('aria-hidden', 'true')
    chevron.classList.add('cs-chevron')
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('fill-rule', 'evenodd')
    path.setAttribute('clip-rule', 'evenodd')
    path.setAttribute('d', 'M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z')
    chevron.appendChild(path)
    trigger.appendChild(chevron)

    const listbox = document.createElement('ul')
    listbox.className = 'cs-listbox'
    listbox.setAttribute('role', 'listbox')
    listbox.tabIndex = -1
    this.buildOptions(listbox, select)

    wrapper.appendChild(trigger)
    wrapper.appendChild(listbox)
    select.parentNode.insertBefore(wrapper, select.nextSibling)

    select._cs = { wrapper, trigger, listbox }

    trigger.addEventListener('click', (e) => {
      e.stopPropagation()
      this.toggle(select)
    })

    trigger.addEventListener('keydown', (e) => this.onTriggerKeydown(e, select))
    listbox.addEventListener('keydown', (e) => this.onListboxKeydown(e, select))

    // Only go full-width if the native select explicitly has w-full
    if (select.classList.contains('w-full')) {
      wrapper.classList.add('cs-full')
    }
  }

  downgrade(select) {
    if (!select._cs) return
    select._cs.wrapper.remove()
    select.style.display = ''
    delete select.dataset.upgraded
    delete select._cs
  }

  // --- Rendering ---

  buildOptions(listbox, select) {
    listbox.innerHTML = ''
    for (const option of select.options) {
      const li = document.createElement('li')
      li.className = 'cs-option'
      li.setAttribute('role', 'option')
      li.dataset.value = option.value
      li.textContent = option.textContent
      if (option.selected) li.classList.add('cs-selected')
      if (option.disabled) {
        li.classList.add('cs-disabled')
        li.setAttribute('aria-disabled', 'true')
      }
      li.addEventListener('click', (e) => {
        e.stopPropagation()
        if (option.disabled) return
        this.selectOption(select, option.value)
      })
      listbox.appendChild(li)
    }
  }

  syncTriggerText(trigger, select) {
    const selected = select.options[select.selectedIndex]
    const chevron = trigger.querySelector('.cs-chevron')
    // Clear all children except chevron
    while (trigger.firstChild && trigger.firstChild !== chevron) {
      trigger.removeChild(trigger.firstChild)
    }
    const text = document.createElement('span')
    text.className = 'cs-value'
    text.textContent = selected ? selected.textContent : ''
    trigger.insertBefore(text, chevron)
  }

  syncTriggerWidth(trigger, select) {
    const measurer = document.createElement('span')
    measurer.style.cssText =
      'visibility:hidden;position:absolute;white-space:nowrap;' +
      'font-family:inherit;font-size:13px;'
    document.body.appendChild(measurer)
    let maxWidth = 0
    for (const option of select.options) {
      measurer.textContent = option.textContent
      const w = measurer.getBoundingClientRect().width
      if (w > maxWidth) maxWidth = w
    }
    measurer.remove()
    // padding (0.5rem * 2) + gap (0.5rem) + chevron (~10px) + buffer
    trigger.style.minWidth = Math.ceil(maxWidth + 40) + 'px'
  }

  // --- Interactions ---

  toggle(select) {
    const { wrapper, listbox } = select._cs
    const isOpen = wrapper.classList.contains('cs-open')
    this.closeAll()
    if (!isOpen) {
      wrapper.classList.add('cs-open')
      const selected = listbox.querySelector('.cs-selected')
      if (selected) {
        this.focusItem(listbox, selected)
        selected.scrollIntoView({ block: 'nearest' })
      } else {
        const first = listbox.querySelector('.cs-option:not(.cs-disabled)')
        if (first) this.focusItem(listbox, first)
      }
    }
  }

  selectOption(select, value) {
    select.value = value
    select.dispatchEvent(new Event('change', { bubbles: true }))
    const { trigger, listbox, wrapper } = select._cs
    this.syncTriggerText(trigger, select)
    listbox.querySelectorAll('.cs-option').forEach(li => {
      li.classList.toggle('cs-selected', li.dataset.value === value)
    })
    wrapper.classList.remove('cs-open')
    trigger.focus()
  }

  closeAll() {
    this.upgradedSelects.forEach(select => {
      if (select._cs) select._cs.wrapper.classList.remove('cs-open')
    })
  }

  onKeydownGlobal(e) {
    if (e.key === 'Escape') this.closeAll()
  }

  onTriggerKeydown(e, select) {
    const { wrapper, listbox } = select._cs
    if (['ArrowDown', 'ArrowUp', ' ', 'Enter'].includes(e.key)) {
      e.preventDefault()
      if (!wrapper.classList.contains('cs-open')) {
        this.toggle(select)
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const focused = listbox.querySelector('.cs-focused') ||
          listbox.querySelector('.cs-selected')
        if (focused) {
          this.moveFocus(listbox, focused, e.key === 'ArrowDown' ? 1 : -1)
        }
      }
    }
  }

  onListboxKeydown(e, select) {
    const { listbox, wrapper } = select._cs
    const focused = listbox.querySelector('.cs-focused')
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (focused) {
        this.moveFocus(listbox, focused, e.key === 'ArrowDown' ? 1 : -1)
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (focused && !focused.classList.contains('cs-disabled')) {
        this.selectOption(select, focused.dataset.value)
      }
    } else if (e.key === 'Escape') {
      wrapper.classList.remove('cs-open')
      select._cs.trigger.focus()
    }
  }

  focusItem(listbox, item) {
    listbox.querySelectorAll('.cs-option').forEach(li =>
      li.classList.remove('cs-focused')
    )
    item.classList.add('cs-focused')
    item.scrollIntoView({ block: 'nearest' })
  }

  moveFocus(listbox, current, direction) {
    const items = Array.from(
      listbox.querySelectorAll('.cs-option:not(.cs-disabled)')
    )
    const idx = items.indexOf(current)
    const next = items[idx + direction]
    if (next) this.focusItem(listbox, next)
  }
}
